import {
    IMenuItem,
    Menu,
    MenuItemValidator,
    MenuItemMiddleware,
    MenuItemTrigger,
    MenuItemContext,
    ValidatorPriority
} from 'sharebase/domain';
import { CommonUtilsService } from 'util/services';
import { 
    Action,
    NestedArray 
} from 'util/domain';
import { MenuPriority } from './menu';
import { MenuFactory } from 'sharebase/factories'

export class ContextMenuItem implements IMenuItem {
    protected validators:MenuItemValidator<any>[] = []; 
    protected triggers:MenuItemTrigger[] = [];
    protected middleware:MenuItemMiddleware[] = [];
    protected _visible:boolean = false;

    public context:MenuItemContext = MenuItemContext.None;
    public priority:MenuPriority = MenuPriority.MEDIUM;

    constructor (
        protected utils:CommonUtilsService,
        protected $q:ng.IQService,
        public action:string, 
        public label:string, 
        public icon:string
    ){
    }

    public get visible() {
        return this._visible;
    }

    public trigger(target, event:ng.IAngularEvent):ng.IPromise<any>|void{
        target = this.applyMiddleware(target);
        return this.$q.all<any>(this.triggers.map(trigger => {
            return trigger.call(this, target, event);
        }));
    }

    public addTrigger(trigger:MenuItemTrigger){
        this.triggers.push(trigger);

        return () => {
            this.removeTrigger(trigger);
        };
    }

    public addValidator(validator:MenuItemValidator<any>, priority:ValidatorPriority = ValidatorPriority.Medium):Action {				
        validator.priority = priority;
        this.validators.push(validator);
        this.validators.sort((v1, v2) => {
            let order = 0;
            if(v1.priority !== v2.priority) {
                order = v1.priority < v2.priority ? 1 : -1;
            }

            return order;
        });
        return () => {
            this.removeValidator(validator);
        };
    }

    public removeAllValidators(){
        this.validators = [];
        return this;
    }

    public removeAllTriggers(){
        this.triggers = [];
        return this;
    }

    public removeAllMiddleware(){
        this.middleware = [];
        return this;
    }

    /*
    *	Add a function that will be called in a pipeline for transforming trigger and validator data
    */
    public addMiddleware(middleware:MenuItemMiddleware) {
        this.middleware.push(middleware);

        return () => {
            this.middleware = this.middleware.filter(m => m !== middleware);
            middleware = null;
        };	
    }

    public canShow(target?:any): ng.IPromise<boolean> {
        target = this.applyMiddleware(target);
        let targets = Array.isArray(target) ? target : [target];    
        targets = targets.length < 1 ? [undefined] : targets;
        let self = this;
        let reachedFailure = false;
        let validatorPromises:NestedArray<() => ng.IPromise<boolean>> = this.validators
            .map(v => {
                return targets.map(t => {
                    // create a function that returns a promise instead of the promise itself
                    // so that resolution is delayed and can be resolved or aborted serially in the future
                    let producer = () => this.$q.resolve(v.call(this, t, targets, target));
                    return producer;
                });
            });

        return this.utils										
            .resolveSync(this.utils.flatten(validatorPromises),(val, i) => {
                // short circuit validator execution if one returns a failure
                if(val === false){
                    reachedFailure = true;
                }

                return val;
            })
            .then(statuses => {
                let visible = reachedFailure ? false : statuses.reduce((all, curr) => {
                    // implicitly treat no return as a passing validator
                    if(!angular.isDefined(curr)){
                        curr = true;
                    }
                    return all && curr;
                }, true);

                return self._visible = visible;
            });
    }

    /**
     * Implements a custom cloning scheme that shallow copies some references (DI'd services)
     * and deep copies instance data. Prefer this over angular.copy() and other deep copying algorithms
     * to maintain shared references.
     */
    public clone() {
        let clone:IMenuItem = new ContextMenuItem(
            this.utils,
            this.$q,
            this.action,
            this.label,
            this.icon
        );

        return this.copyInstanceData(clone);
    }

    /**
     * Shallow copies the instance data of this item, such as context and validators, 
     * to the specified menu item.
     * @param clone The destination menu item to have this menu item's instance data copied over to.
     */
    protected copyInstanceData(clone:IMenuItem){
        clone.context = this.context;
        this.validators.forEach(v => clone.addValidator(v));
        this.triggers.forEach(t => clone.addTrigger(t));
        this.middleware.forEach(m => clone.addMiddleware(m));
        return clone;
    }

    private removeValidator(toRemove:MenuItemValidator<any>){
        this.validators = this.validators.filter(v => v !== toRemove);
    }

    private removeTrigger(toRemove:MenuItemTrigger){
        this.triggers = this.triggers.filter(t => t !== toRemove);
    }

    private applyMiddleware(target){
        return this.middleware.reduce((previous, middleware) => {
            return middleware(previous);
        }, target);
    }
}	

const DUMMY = 'DUMMY';
/**
 * Represents a menu item that can exist in one of two states, depending on validators passing.
 */
export class DualMenuItem extends ContextMenuItem {
    private items:IMenuItem[] = [];
    private activeItem:IMenuItem;
    private _context:MenuItemContext;
    private _label:string = '';

    public set icon(val){}
    public get icon(){
        return this.activeItem.icon;
    }
    
    public set label(val) {
        this._label = val;
    }
    public get label(){
        if(this._label){
            return this._label;
        }
        else {
            return this.activeItem.label;
        }        
    }

    public get visible() {
        return this.activeItem.visible && this._visible;
    }

    public set context(val:MenuItemContext){
        this._context = val;
        if(this.items){
            this.items.forEach(i => i.context = val);
        }
    }
    public get context() {
        return this._context;
    }

    public constructor(
        utils:CommonUtilsService,
        $q:ng.IQService,
        public action:string,        
        item1:IMenuItem,
        item2:IMenuItem
    ){
        super(utils, $q, DUMMY, DUMMY, DUMMY);
        this.items = [item1, item2];
        this.activeItem = this.items[0];
    }

    public clone(){
        let clone = new DualMenuItem(
            this.utils,
            this.$q,
            this.action,
            this.items[0].clone(),
            this.items[1].clone()
        );

        return this.copyInstanceData(clone);
    }

    public canShow(target:any){
        return super.canShow(target).then(canShow => {
            let result = this.$q.resolve(false);
            if(canShow){
                result = this.determineActiveItem(target);
            }

            return result;
        });
    }

    public trigger(target, event:ng.IAngularEvent){ 
        super.trigger(target, event);
        let p = this.$q.resolve(this.activeItem.trigger(target, event));
        return p.then(() => {
            this.determineActiveItem(target);
        });     
    }

    public getState(actionName:string){
        let item = this.items.filter(i => i.action === actionName)[0];
        if(!item){
            throw new Error(`Unable to get state for specified action name: ${actionName}`);
        }

        return item;
    }

    public removeAllTriggers(){
        super.removeAllTriggers();
        this.items.forEach(i => i.removeAllTriggers());
        return this;
    }

    public removeAllValidators(){
        super.removeAllValidators();
        this.items.forEach(i => i.removeAllValidators());
        return this;
    }

    public removeAllMiddleware(){
        super.removeAllMiddleware();
        this.items.forEach(i => i.removeAllMiddleware());
        return this;
    }

    private determineActiveItem(target){
        let promises = this.items.map(i => i.canShow(target));
        return this.$q.all<boolean>(promises)
            .then(results => {
                let canShow = false;
                for(let i = 0; i < results.length; i++){
                    let val = results[i];
                    if(val === true){
                        this.activeItem = this.items[i];
                        canShow = true;
                        break;
                    }
                }

                return canShow;
            });
    }
}

export class NestedMenuItem extends ContextMenuItem {
	public menu:Menu;
	
	constructor(
		utils:CommonUtilsService,
		$q:ng.IQService,
		sbMenuFactory:MenuFactory,
        public action:string, 
        public label:string, 
		public icon:string
	){
		super(utils, $q, action, label, icon);
		this.menu = sbMenuFactory.create();
	}

	public canShow(target?:any){
		return super.canShow(target)
			.then(canShow => {
				// short circuit expensive calculation of child items if own validators fail
				if(!canShow){
					return false;
				}

				return this.menu.getActiveItems(target).then(items => items.length > 0);
			})
			.then(canShow => this._visible = canShow);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase/domain/contextMenuItem.ts